import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { QuickLinksSearchModalComponent } from '../components/quick-links/quick-links-search-modal/quick-links-search-modal.component';
import { DynamicSearchDialogComponent } from '../components/quick-links/dynamic-search-dialog/dynamic-search-dialog.component';

export interface QuickLink {
  key: string;
  description: string;
  route?: string;
  action?: () => void;
  icon?: string;
}

@Injectable({
  providedIn: 'root'
})
export class QuickLinksService {
  private quickLinks: QuickLink[] = [];
  private quickLinksSubject = new BehaviorSubject<QuickLink[]>([]);
  private showQuickLinksSubject = new BehaviorSubject<boolean>(false);

  quickLinks$ = this.quickLinksSubject.asObservable();
  showQuickLinks$ = this.showQuickLinksSubject.asObservable();

  constructor(
    private router: Router,
    private dialog: MatDialog
  ) {
    // Initialize with some default quick links
    this.addQuickLink({
      key: 'F1',
      description: 'Dynamic Search',
      icon: 'search',
      action: () => this.openDynamicSearchDialog()
    });

    this.addQuickLink({
      key: 'F2',
      description: 'Tenancy Contract',
      route: '/properties/tenant-contract',
      icon: 'description'
    });

    this.addQuickLink({
      key: 'F3',
      description: 'Property',
      route: '/properties/property',
      icon: 'home'
    });

    this.addQuickLink({
      key: 'F4',
      description: 'Booking List',
      route: '/front_office/booking',
      icon: 'calendar_today'
    });

    this.addQuickLink({
      key: 'F5',
      description: 'Vaccant List',
      route: '/front_office/vacant_units',
      icon: 'home_work'
    });

    this.addQuickLink({
      key: 'F6',
      description: 'Tenant Invoice',
      route: '/transactions/tenant_invoice',
      icon: 'receipt'
    });

    this.addQuickLink({
      key: 'F7',
      description: 'Tenant List',
      route: '/contacts/tenants',
      icon: 'people'
    });

    this.addQuickLink({
      key: 'F8',
      description: 'Utility Bills',
      route: '/properties/utility_bills',
      icon: 'electrical_services'
    });

    // this.addQuickLink({
    //   key: 'F9',
    //   description: 'Settings',
    //   route: '/setting',
    //   icon: 'settings'
    // });

    // this.addQuickLink({
    //   key: 'F10',
    //   description: 'My Info',
    //   route: '/my-info',
    //   icon: 'person'
    // });

    this.addQuickLink({
      key: '?',
      description: 'Show/Hide Quick Links',
      icon: 'keyboard',
      action: () => this.toggleQuickLinks()
    });

    // Initialize the quick links
    this.quickLinksSubject.next(this.quickLinks);

    // Set up keyboard event listener
    this.setupKeyboardListener();
  }

  private setupKeyboardListener(): void {
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      // Check if user is authenticated and not on auth pages
      const token = localStorage.getItem('token');
      const isAuthRoute = window.location.pathname.startsWith('/auth');

      if (!token || isAuthRoute) {
        return; // Don't handle keyboard shortcuts if not authenticated or on auth pages
      }

      // Check if the key is a function key (F1-F12) or the question mark key
      if (event.key.startsWith('F') || event.key === '?') {
        const quickLink = this.quickLinks.find(link => link.key === event.key);
        if (quickLink) {
          event.preventDefault();
          this.activateQuickLink(quickLink);
        }
      }
    });
  }

  addQuickLink(quickLink: QuickLink): void {
    // Check if a quick link with the same key already exists
    const existingIndex = this.quickLinks.findIndex(link => link.key === quickLink.key);

    if (existingIndex >= 0) {
      // Update existing quick link
      this.quickLinks[existingIndex] = quickLink;
    } else {
      // Add new quick link
      this.quickLinks.push(quickLink);
    }

    // Update the subject
    this.quickLinksSubject.next(this.quickLinks);
  }

  removeQuickLink(key: string): void {
    this.quickLinks = this.quickLinks.filter(link => link.key !== key);
    this.quickLinksSubject.next(this.quickLinks);
  }

  activateQuickLink(quickLink: QuickLink): void {
    if (quickLink.action) {
      quickLink.action();
    } else if (quickLink.route) {
      this.router.navigate([quickLink.route]);
    }
  }

  toggleQuickLinks(): void {
    const currentValue = this.showQuickLinksSubject.value;
    this.showQuickLinksSubject.next(!currentValue);
  }

  openSearchModal(): void {
    this.dialog.open(QuickLinksSearchModalComponent, {
      width: '600px',
      data: { quickLinks: this.quickLinks }
    });
  }

  openDynamicSearchDialog(): void {
    this.dialog.open(DynamicSearchDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: false,
      autoFocus: false,
      panelClass: 'dynamic-search-dialog-panel'
    });
  }
}
